import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import type { BulkConfirmDialogData } from "../contexts/bulkAssignmentDialog.context";

interface Props {
	isOpen: boolean;
	info: BulkConfirmDialogData | null;
	onConfirm: () => void;
	onCancel: () => void;
}

/**
 * A dialog for confirming bulk assignments
 * @param props.isOpen - Whether the dialog is open.
 * @param props.info - The information to display.
 * @param props.onConfirm - The function to call when the confirm button is clicked.
 * @param props.onCancel - The function to call when the cancel button is clicked.
 */
export function BulkAssignmentConfirmDialog({
	isOpen,
	info,
	onConfirm,
	onCancel,
}: Props) {
	if (!info) {
		return (
			<Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
				<DialogContent className="sm:max-w-[500px] bg-background-secondary">
					<DialogHeader>
						<DialogTitle>Aktion bestätigen</DialogTitle>
						<DialogDescription>Keine Details verfügbar.</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="outline" onClick={onCancel}>
							Abbrechen
						</Button>
						<Button onClick={onConfirm} className="bg-red-600 hover:bg-red-700">
							Bestätigen
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		);
	}

	const affectedCount = info.affectedDetails.length;
	const duplicateCount = info.duplicateDetails.length;
	const totalSelected = info.totalSelected;
	const stepNumber = info.stepNumber;

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
			<DialogContent className="sm:max-w-[500px] bg-background-secondary">
				<DialogHeader>
					<DialogTitle>
						{info.isRemoval
							? "Zuweisungen entfernen?"
							: "Zuweisungen vornehmen?"}
					</DialogTitle>
					<DialogDescription asChild>
						<div>
							{info.isRemoval ? (
								<div>
									{affectedCount > 0 ? (
										<div>
											<div className="mb-3">
												Von {totalSelected} ausgewählten Klausuren haben{" "}
												<span className="font-medium">{affectedCount}</span>{" "}
												eine aktive Zuweisung für Korrekturschritt {stepNumber}:
											</div>
											<div className="max-h-32 overflow-y-auto bg-slate-50 p-3 rounded-md border">
												<ul className="text-sm space-y-1">
													{info.affectedDetails.map((detail) => (
														<li key={detail} className="text-slate-700">
															• {detail}
														</li>
													))}
												</ul>
											</div>
											<p className="mt-3">
												Möchten Sie diese Zuweisungen wirklich entfernen?
											</p>
										</div>
									) : (
										<p>
											Keine der {totalSelected} ausgewählten Klausuren hat eine
											aktive Zuweisung für Korrekturschritt {stepNumber}.
										</p>
									)}
								</div>
							) : (
								<div>
									{affectedCount > 0 && (
										<>
											<div className="mb-3">
												Von {totalSelected} ausgewählten Klausuren haben{" "}
												<span className="font-medium">{affectedCount}</span>{" "}
												bereits eine Zuweisung für Korrekturschritt {stepNumber}
												:
											</div>
											<div className="max-h-32 overflow-y-auto bg-slate-50 p-3 rounded-md border">
												<ul className="text-sm space-y-1">
													{info.affectedDetails.map((detail) => (
														<li key={detail} className="text-slate-700">
															• {detail}
														</li>
													))}
												</ul>
											</div>
											<p className="mt-3">
												Möchten Sie diese bestehenden Zuweisungen überschreiben?
											</p>
										</>
									)}
									{duplicateCount > 0 && (
										<div className="mt-4">
											<div className="mb-2">
												<span className="font-medium">{duplicateCount}</span>{" "}
												Zuweisung(en) würden zu doppelter Zuweisung in anderen
												Schritten führen und werden übersprungen:
											</div>
											<div className="max-h-32 overflow-y-auto bg-amber-50 p-3 rounded-md border border-amber-200">
												<ul className="text-sm space-y-1 text-amber-800">
													{info.duplicateDetails.map((detail) => (
														<li key={detail}>• {detail}</li>
													))}
												</ul>
											</div>
											<p className="mt-2 text-sm text-slate-600">
												{info.applicableCount} Zuweisung(en) führen zu keinen
												Konflikten mit anderen Zuweisungen.
											</p>
										</div>
									)}
								</div>
							)}
						</div>
					</DialogDescription>
				</DialogHeader>
				<DialogFooter>
					<Button variant="outline" onClick={onCancel}>
						Abbrechen
					</Button>
					<Button
						onClick={onConfirm}
						className="bg-red-600 hover:bg-red-700"
						disabled={
							(info.isRemoval && info.affectedDetails.length === 0) ||
							(!info.isRemoval && info.applicableCount === 0)
						}
					>
						{info.isRemoval ? "Zuweisungen entfernen" : "Zuweisungen vornehmen"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
